import * as recall from '@otter-ai/recall-client';
import type { AdditionalQueryOptions } from '@/shared/utils/queryCreators';

type TemplatesData = {
  data: {
    templates: recall.Template[];
    permissions: recall.GetTemplatesPermissions;
  };
};

type TemplatesQueryKey =
  | ['templates']
  | ['templates', 'personal']
  | ['templates', 'workspace'];

type AdditionalTemplatesQueryOptions = AdditionalQueryOptions<
  TemplatesData,
  TemplatesQueryKey
>;

export type TemplatesQuery = {
  readonly queryKey: TemplatesQueryKey;
  readonly queryFn: () => Promise<TemplatesData>;
} & AdditionalTemplatesQueryOptions;

/**
 * Query creator for fetching templates
 *
 * @example
 * ```tsx
 * // Fetch all templates
 * const { data: templates } = useQuery(templatesQueryCreator());
 *
 * // Fetch personal templates only
 * const { data: personalTemplates } = useQuery(templatesQueryCreator('personal'));
 *
 * // Fetch workspace templates only
 * const { data: workspaceTemplates } = useQuery(templatesQueryCreator('workspace'));
 *
 * // with queryClient
 * const templates = await queryClient.fetchQuery(templatesQueryCreator());
 * queryClient.invalidateQueries(templatesQueryCreator());
 * queryClient.setQueryData(templatesQueryCreator().queryKey, newTemplatesData);
 * ```
 *
 * @param filter Optional filter to get only personal or workspace templates
 * @param additionalOptions Additional options that can be passed to useQuery
 */
export function templatesQueryCreator(
  filter?: 'personal' | 'workspace',
  additionalOptions: AdditionalTemplatesQueryOptions = {},
): TemplatesQuery {
  const queryKey:
    | ['templates']
    | ['templates', 'personal']
    | ['templates', 'workspace'] = filter
    ? ['templates', filter]
    : ['templates'];

  return {
    queryKey,
    queryFn: async () => {
      const queryParams =
        filter === 'personal'
          ? { is_personal_template: true }
          : filter === 'workspace'
            ? { is_personal_template: false }
            : undefined;

      const response = await recall.getTemplates(
        queryParams ? { query: queryParams } : undefined,
      );
      if (response.error) {
        throw response.error;
      }
      return response.data;
    },
    refetchOnWindowFocus: false,
    ...additionalOptions,
  };
}
